import React, { useCallback, useContext, useEffect, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";

import { getHotkeyLabel, useHotkeys } from "fond/hooks/useHotkeys";

import { MapContext } from "../MapProvider";

import { IButtonProps } from "./Toolbar";
import ToolbarButton from "./ToolbarButton";
import { useLayerEditable } from "./useLayerEditable";

const DeleteButton: React.FC<IButtonProps> = ({ icon, title = "Delete feature(s)" }) => {
  const { map, drawControl } = useContext(MapContext);
  const [hasSelection, setHasSelection] = useState(false);
  const isLayerEditable = useLayerEditable();

  /**
   * Monitor when the selectionchange event occurs so that we can disabled
   * this feature based on the number of selected features.
   */
  const handleOnSelect = useCallback(() => {
    setHasSelection((drawControl.current?.getSelectedIds().length || 0) > 0);
  }, [drawControl]);

  useEffect(() => {
    map?.on("draw.selectionchange", handleOnSelect);

    return () => {
      map?.off("draw.selectionchange", handleOnSelect);
    };
  }, [map, handleOnSelect]);

  const handleOnClick = () => {
    drawControl.current?.trash();
  };

  useHotkeys({
    action: "DELETE",
    callback: (event) => {
      event.preventDefault();
      handleOnClick();
    },
    enabled: hasSelection && isLayerEditable,
  });

  const tooltipTitle = `${title} (${getHotkeyLabel("DELETE")})`;

  return (
    <ToolbarButton title={tooltipTitle} onClick={handleOnClick} disabled={!hasSelection || !isLayerEditable} data-testid="toolbar-delete-button">
      {icon || <DeleteIcon />}
    </ToolbarButton>
  );
};

export default DeleteButton;
